import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import {
  DEFAULT_LOCALE,
  localeForBareRoot,
  localizeHref,
  localizePathname,
  normalizeLocale,
  prefixForLocale,
  stripLocalePrefix,
  writeLocalePreference,
  type LocaleTag
} from "./locale";

interface LocaleContextValue {
  locale: LocaleTag;
  barePathname: string;
  href: (href: string) => string;
  pathname: (pathname: string) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleRoute({ prefixed = false }: { prefixed?: boolean }) {
  const { locale: routeLocale } = useParams();
  const location = useLocation();
  const locale = prefixed ? normalizeLocale(routeLocale) : DEFAULT_LOCALE;

  if (!locale || (prefixed && prefixForLocale(locale) !== routeLocale?.toLowerCase())) {
    return <Navigate replace to="/" />;
  }

  if (!prefixed && location.pathname === "/") {
    const preferred = localeForBareRoot(
      typeof window === "undefined" ? null : window.localStorage,
      typeof navigator === "undefined" ? [] : navigator.languages
    );
    if (preferred !== DEFAULT_LOCALE) {
      return <Navigate replace to={`${localizePathname("/", preferred)}${location.search}`} />;
    }
  }

  return <ActiveLocaleRoute locale={locale} />;
}

function ActiveLocaleRoute({ locale }: { locale: LocaleTag }) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const initialMetadataPreserved = useRef(false);

  const barePathname = stripLocalePrefix(location.pathname);
  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      barePathname,
      href: (href) => localizeHref(href, locale),
      pathname: (pathname) => localizePathname(pathname, locale)
    }),
    [barePathname, locale]
  );

  useEffect(() => {
    let active = true;
    const applyLocale = async () => {
      if (i18n.resolvedLanguage !== locale) {
        await i18n.changeLanguage(locale);
      }
      if (!active) return;
      const translate = i18n.getFixedT(locale);
      writeLocalePreference(locale);
      document.documentElement.lang = locale;
      // Flask renders canonical, route-specific metadata. Keep it intact during
      // hydration, then update metadata for client-side route/locale changes.
      if (initialMetadataPreserved.current) {
        const section =
          barePathname === "/map" ? "map"
            : barePathname === "/about" ? "about"
              : barePathname === "/methodology" ? "methodology"
                : barePathname === "/privacy" ? "privacy"
                  : "home";
        document.title = translate(`meta.${section}.ogTitle`);
        const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
        if (description) description.content = translate(`meta.${section}.description`);
      } else {
        initialMetadataPreserved.current = true;
      }
    };
    void applyLocale();
    return () => {
      active = false;
    };
  }, [barePathname, i18n, locale]);

  return (
    <LocaleContext.Provider value={value}>
      <AppShell />
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const value = useContext(LocaleContext);
  return value ?? {
    locale: DEFAULT_LOCALE,
    barePathname: "/",
    href: (href: string) => localizeHref(href, DEFAULT_LOCALE),
    pathname: (pathname: string) => localizePathname(pathname, DEFAULT_LOCALE)
  };
}
